import type { ReviewsBlock } from '$models/Block/ReviewsBlock';
import type { LandingPage } from '$models/Page/LandingPage';
import type { Page } from '$models/Page/Page';
import { sanitizeHtmlToPlainText } from '$lib/utils';

export type OrganizationReviewInput = {
	authorName: string;
	datePublished: string;
	reviewBody: string;
	ratingValue: number;
	platform?: string;
};

export type OrganizationReviewBuildInput = {
	aggregateRating?: { ratingValue: string; reviewCount: string; bestRating?: string } | null;
	reviews?: OrganizationReviewInput[] | null;
};

export function findReviewsBlockOnLandingPage(page: Page): ReviewsBlock | null {
	if (page.type !== 'landing') {
		return null;
	}
	const block = (page as LandingPage).blocks.find((b) => b.type === 'reviews');
	return block ? (block as ReviewsBlock) : null;
}

export function mapReviewsBlockToOrganizationInputs(block: ReviewsBlock): {
	aggregateRating: { ratingValue: string; reviewCount: string; bestRating: string };
	reviews: OrganizationReviewInput[];
} {
	const reviews: OrganizationReviewInput[] = block.reviews.slice(0, 10).map((r) => {
		const d =
			r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt as unknown as string);
		return {
			authorName: sanitizeHtmlToPlainText(r.reviewerName) || 'Customer',
			datePublished: Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10),
			reviewBody: sanitizeHtmlToPlainText(r.text),
			ratingValue: r.rating,
			platform: r.platform
		};
	});
	return {
		aggregateRating: {
			ratingValue: String(block.averageRating),
			reviewCount: String(block.amountOfReviews),
			bestRating: '5'
		},
		reviews
	};
}

export function mergeOrganizationReviewsIntoSchema(
	schema: Record<string, unknown>,
	input: OrganizationReviewBuildInput
): void {
	if (input.aggregateRating?.ratingValue && input.aggregateRating?.reviewCount) {
		schema.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: input.aggregateRating.ratingValue,
			reviewCount: input.aggregateRating.reviewCount,
			bestRating: input.aggregateRating.bestRating ?? '5'
		};
	}

	if (input.reviews?.length) {
		schema.review = input.reviews
			.filter((r) => r.reviewBody && r.datePublished)
			.map((r) => {
				const publisherName = r.platform === 'trusted' ? 'eTrusted' : undefined;
				const row: Record<string, unknown> = {
					'@type': 'Review',
					author: {
						'@type': 'Person',
						name: r.authorName
					},
					datePublished: r.datePublished,
					reviewBody: r.reviewBody,
					reviewRating: {
						'@type': 'Rating',
						ratingValue: String(r.ratingValue)
					}
				};
				if (publisherName) {
					row.publisher = {
						'@type': 'Organization',
						name: publisherName
					};
				}
				return row;
			});
	}
}
